<template>
  <div class="checkbox-mobilelike">
    <label
      :class="{
        'checkbox-mobilelike__label-wrapper': true,
        'checkbox-mobilelike__label-wrapper--tiny': size === 'tiny',
        'checkbox-mobilelike__label-wrapper--small': size === 'small',
      }"
    >
      <input
        v-bind="$attrs"
        ref="input"
        v-model="checked"
        class="checkbox-mobilelike__checkbox-input"
        type="checkbox"
      />

      <!-- Checkbox imitation -->
      <span
        :class="{
          'checkbox-imitation': true,
          'checkbox-imitation--tiny': size === 'tiny',
          'checkbox-imitation--small': size === 'small',
          'checkbox-imitation--gray': color === 'gray',
          'checkbox-imitation--inactive': !checked,
          'checkbox-imitation--active': checked,
          'checkbox-mobilelike__checkbox-imitation': true,
        }"
      >
        <transition name="simple-fade-in-out">
          <span
            v-if="checked"
            :class="{
              'checkbox-imitation__icon': true,
              'checkbox-imitation__icon--tiny': size === 'tiny',
              'checkbox-imitation__icon--small': size === 'small',
              'checkbox-imitation__icon--checkmark': true,
            }"
            v-html="checkMarkIcon"
          >
          </span>
        </transition>

        <transition name="simple-fade-in-out">
          <span
            v-if="!checked"
            :class="{
              'checkbox-imitation__icon': true,
              'checkbox-imitation__icon--tiny': size === 'tiny',
              'checkbox-imitation__icon--small': size === 'small',
              'checkbox-imitation__icon--cross': true,
            }"
            v-html="icoCrossIcon"
          >
          </span>
        </transition>

        <span
          :class="{
            'checkbox-imitation__handler': true,
            'checkbox-imitation__handler--tiny': size === 'tiny',
            'checkbox-imitation__handler--small': size === 'small',
            'checkbox-imitation__handler--checked-position': checked,
          }"
        ></span> </span
      ><!-- / Checkbox imitation -->
    </label>
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue';
import checkMarkIcon from '@icons/other/check-mark-icon.svg';
import icoCrossIcon from '@icons/other/ico-cross.svg';

const checked = defineModel<boolean>({ required: true });

const { size = 'large' } = defineProps<{
  size?: 'tiny' | 'small' | 'medium' | 'large';
  color?: 'gray';
}>();

const input = useTemplateRef<HTMLInputElement>('input');

// Exposed so whatever opens a switch can hand it the keyboard. It is a real checkbox
// underneath, so Space already flips it once it is the focused element.
defineExpose({ focus: () => input.value?.focus({ preventScroll: true }) });
</script>

<style scoped lang="scss">
@import '@styles/variables';
@import '@styles/media-queries';
@import '@styles/text';

// ============================================================================
// ============================================================================
// Checkbox imitation
.checkbox-imitation {
  width: 6.5rem;
  height: 3rem;
  display: block;
  border-radius: 10rem;
  position: relative;
  transition: background-color 0.1s ease;

  &--inactive {
    background: rgb(245, 115, 110);
  }

  &--active {
    background: $c-dark;
  }

  &--gray.checkbox-imitation--inactive {
    background: #ccd1d6;
  }

  &--tiny {
    width: 2.8rem;
    height: 1.5rem;
  }

  &--small {
    width: 3.7rem;
    height: 1.8rem;
  }

  &__icon {
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    position: absolute;

    :deep(svg) {
      width: 100%;
      height: 100%;
      fill: white;
    }

    &--checkmark {
      width: 1.8rem;
      height: 1.8rem;
      inset: calc(50% - 0.9rem) auto auto 1.2rem;
    }
    &--cross {
      width: 1.4rem;
      height: 1.4rem;
      inset: calc(50% - 0.7rem) 1.3rem auto auto;
    }

    &--tiny.checkbox-imitation__icon--checkmark {
      display: none;
    }
    &--tiny.checkbox-imitation__icon--cross {
      display: none;
    }

    &--small.checkbox-imitation__icon--checkmark {
      width: 1.2rem;
      height: 1.2rem;
      inset: 3px auto auto 0.6rem;
    }
    &--small.checkbox-imitation__icon--cross {
      width: 1rem;
      height: 1rem;
      inset: 4px 0.7rem auto auto;
    }
  }

  &__handler {
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 10rem;
    position: absolute;
    top: 0.3rem;
    left: 0.4rem;
    background: white;
    transition: left 0.1s ease;

    &--checked-position {
      left: calc(100% - 2.4rem - 0.4rem);
    }

    &--tiny {
      width: 1.1rem;
      height: 1.1rem;
      inset: 2px auto auto 3px;
    }
    &--tiny.checkbox-imitation__handler--checked-position {
      left: 1.4rem;
    }

    &--small {
      width: 1.4rem;
      height: 1.4rem;
      inset: 2px auto auto 3px;
    }
    &--small.checkbox-imitation__handler--checked-position {
      left: 2rem;
    }
  }
}

// ============================================================================
// ============================================================================
// Checkbox mobilelike
.checkbox-mobilelike {
  user-select: none;

  &__label-wrapper {
    height: 3rem;
    min-height: 3rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-content: center;
    align-items: center;
    position: relative;
    cursor: pointer;

    &--tiny {
      height: 1.5rem;
      min-height: 1.5rem;
    }

    &--small {
      height: 1.8rem;
      min-height: 1.8rem;
    }

    &::before {
      content: '';
      width: calc(100% + 1rem);
      height: calc(100% + 1rem);
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &__checkbox-input {
    position: absolute;
    opacity: 0;
  }

  &__checkbox-imitation {
  }
}
</style>
